/**
 * Move-in / move-out request — type segmented, date, slot, vehicle,
 * helpers, notes, plus existing requests with status pills below the form.
 * Wired: GET + POST /api/services/move-requests. Port of MoveInOut in
 * design/source/screens-services.jsx.
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

import { space } from '@bero/tokens';
import { fmtWhen, str, useGateList } from '@/components/gate/api';
import { DateGrid, Segmented } from '@/components/gate/bits';
import { Icon } from '@/components/gate/icons';
import { useT, translate, monthShort, dayShort } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, Field, Input, Pill, Screen, SectionLabel, Sheet, Skeleton, TopBar } from '@/ui';

const WHEN = ['Today', 'Tomorrow', 'Pick a date'];
const WHEN_LABEL: Record<string, DictKey> = {
  Today: 'pa_when_today', Tomorrow: 'pa_when_tomorrow', 'Pick a date': 'pa_when_pickdate',
};
// Move type: 'in'/'out' logic value kept; display resolved via t() at render.
const MO_TYPE_LABEL: Record<string, DictKey> = { in: 'mo_movein', out: 'mo_moveout' };

function isoToday(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type MoveRequest = {
  id: string; type: string; date: string; slot: string;
  status: 'open' | 'progress' | 'approved' | 'pending' | 'rejected';
  cancelled: boolean;
  vehicleNo: string; helpers: string; notes: string; submitted: string;
};

function statusOf(v: string): MoveRequest['status'] {
  const s = v.toLowerCase();
  if (s === 'approved') return 'approved';
  if (s === 'rejected' || s === 'declined') return 'rejected';
  if (s === 'in_progress' || s === 'progress') return 'progress';
  return 'pending';
}

// month/day names via i18n monthShort/dayShort (localised with active language)

/** Move dates are date-only (YYYY-MM-DD) — format without a time of day
 * ("Today" / "Tomorrow" / "Sun 20 Jul"), never through fmtWhen which would
 * invent a 5:30 AM from UTC midnight. */
function fmtMoveDate(raw?: string): string {
  if (!raw) return '';
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return fmtWhen(raw);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return translate('time_today');
  if (diff === 1) return translate('pa_when_tomorrow');
  return `${dayShort(d.getDay())} ${d.getDate()} ${monthShort(d.getMonth())}`;
}

function toRequest(r: unknown, i: number): MoveRequest {
  const rawType = (str(r, 'type', 'requestType') ?? 'in').toLowerCase();
  const rawStatus = (str(r, 'status') ?? 'pending').toLowerCase();
  return {
    id: str(r, 'id') ?? `mr${i}`,
    type: rawType === 'out' ? 'out' : 'in', // raw; localised at render
    date: fmtMoveDate(str(r, 'date', 'preferredDate', 'createdAt')),
    slot: str(r, 'timeSlot', 'slot') ?? '',
    status: statusOf(rawStatus),
    cancelled: rawStatus === 'cancelled',
    vehicleNo: str(r, 'vehicleNo') ?? '',
    helpers: str(r, 'helpers') ?? '',
    notes: str(r, 'notes') ?? '',
    submitted: fmtWhen(str(r, 'createdAt')),
  };
}

export default function MoveInOutScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, state, reload } = useGateList<unknown>('/api/services/move-requests', 'requests', 'moveRequests');
  const rows = items.map(toRequest);

  const [type, setType] = React.useState('in'); // 'in' | 'out'
  const [when, setWhen] = React.useState('Today');
  const [pickedDate, setPickedDate] = React.useState('');
  const [slot, setSlot] = React.useState('');
  const [vehicle, setVehicle] = React.useState('');
  const [helpers, setHelpers] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [noticeIsError, setNoticeIsError] = React.useState(false);
  const [selected, setSelected] = React.useState<MoveRequest | null>(null);
  const [cancelling, setCancelling] = React.useState(false);
  const [sheetError, setSheetError] = React.useState<string | null>(null);

  const cancelRequest = async () => {
    if (!selected || cancelling) return;
    setCancelling(true);
    setSheetError(null);
    try {
      await api.post<unknown>(`/api/services/move-requests/${encodeURIComponent(selected.id)}`, { action: 'cancel' });
      setSelected(null);
      await reload();
    } catch {
      setSheetError(t('mo_cancel_error'));
    } finally {
      setCancelling(false);
    }
  };

  const dateValue = () => {
    if (when === 'Tomorrow') return isoToday(1);
    if (when === 'Pick a date') return pickedDate;
    return isoToday(0);
  };

  const canSave = dateValue().length > 0 && slot.trim().length > 0;

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setNotice(null);
    setNoticeIsError(false);
    try {
      await api.post<unknown>('/api/services/move-requests', {
        type,
        date: dateValue(),
        timeSlot: slot.trim(),
        vehicleNo: vehicle.trim() || undefined,
        helpers: helpers.trim() ? Number(helpers.trim()) : undefined,
        notes: notes.trim() || undefined,
      });
      setWhen('Today');
      setPickedDate('');
      setSlot('');
      setVehicle('');
      setHelpers('');
      setNotes('');
      setNotice(t('mo_submitted', { type: t(MO_TYPE_LABEL[type]) }));
      await reload();
    } catch (e) {
      const status = (e as { status?: number }).status;
      setNotice(status === 400 ? t('mo_err_details') : t('mo_err_generic'));
      setNoticeIsError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('mo_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4, paddingBottom: space.s8 }}>
        <Field label={t('mo_request_type')}>
          <Segmented options={['in', 'out']} labels={[t('mo_movein'), t('mo_moveout')]} value={type} onChange={setType} />
        </Field>
        <Field label={t('mo_preferred_date')}>
          <Segmented options={WHEN} labels={WHEN.map((w) => t(WHEN_LABEL[w]))} value={when} onChange={setWhen} />
        </Field>
        {when === 'Pick a date' ? (
          <DateGrid value={pickedDate} onChange={setPickedDate} />
        ) : null}
        <Field label={t('mo_time_slot')}>
          <Input value={slot} onChangeText={setSlot} placeholder="10:00 AM – 2:00 PM" />
        </Field>
        <Field label={t('mo_vehicle')}>
          <Input value={vehicle} onChangeText={setVehicle} placeholder="MH 04 GH 9012" autoCapitalize="characters" />
        </Field>
        <Field label={t('mo_helpers')}>
          <Input value={helpers} onChangeText={setHelpers} placeholder="4" keyboardType="numeric" />
        </Field>
        <Field label={t('mo_notes')}>
          <Input
            value={notes}
            onChangeText={setNotes}
            placeholder={t('mo_notes_ph')}
            multiline
            style={{ minHeight: 80, textAlignVertical: 'top' }}
          />
        </Field>
        <View style={{ borderWidth: 1, borderColor: colors.borderSoft, borderRadius: 14, backgroundColor: colors.canvas1, padding: space.s4, gap: space.s2 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <AppText variant="bodySm" tone="secondary">{t('mo_deposit')}</AppText>
            <AppText variant="bodySm" weight="600">{t('mo_applies')}</AppText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <AppText variant="bodySm" tone="secondary">{t('mo_lift')}</AppText>
            <AppText variant="bodySm" weight="600" style={{ color: colors.success500 }}>{t('mo_included')}</AppText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <AppText variant="bodySm" tone="secondary">{t('mo_approval')}</AppText>
            <AppText variant="bodySm" weight="600" style={{ color: colors.warning500 }}>{t('mo_required')}</AppText>
          </View>
        </View>
        {notice ? <AppText variant="bodySm" tone={noticeIsError ? 'danger' : 'secondary'}>{notice}</AppText> : null}
        <Button title={t('mo_submit')} size="lg" disabled={!canSave} loading={saving} onPress={() => void submit()} />

        <SectionLabel style={{ marginTop: space.s2 }}>{t('mo_your_requests')}</SectionLabel>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1].map((i) => <Skeleton key={i} h={64} />)}
          </View>
        ) : rows.length === 0 ? (
          <AppText variant="bodySm" tone="tertiary">{t('mo_no_requests')}</AppText>
        ) : (
          <View style={{ gap: space.s3 }}>
            {rows.map((r) => (
              <Pressable
                key={r.id}
                onPress={() => { setSheetError(null); setSelected(r); }}
                accessibilityRole="button"
                accessibilityLabel={t('mo_request_title', { type: t(MO_TYPE_LABEL[r.type]) })}
              >
                <Card style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <View>
                    <AppText variant="bodyMd" weight="500">{t(MO_TYPE_LABEL[r.type])}</AppText>
                    <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                      {r.date}{r.date && r.slot ? ' · ' : ''}{r.slot}
                    </AppText>
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                    <Pill status={r.cancelled ? 'pending' : r.status} label={r.cancelled ? 'CANCELLED' : undefined} />
                    <Icon name="chevron-right" size={18} color={colors.textTertiary} />
                  </View>
                </Card>
              </Pressable>
            ))}
          </View>
        )}
      </Screen>

      {/* Request detail */}
      <Sheet visible={!!selected} onClose={() => setSelected(null)}>
        {selected ? (
          <>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: space.s4 }}>
              <AppText variant="headingMd" weight="500">{t('mo_request_title', { type: t(MO_TYPE_LABEL[selected.type]) })}</AppText>
              <Pill status={selected.cancelled ? 'pending' : selected.status} label={selected.cancelled ? 'CANCELLED' : undefined} />
            </View>
            <View style={{ gap: space.s2 }}>
              {[
                [t('mo_date'), selected.date],
                [t('mo_time_slot'), selected.slot],
                [t('mo_goods_vehicle'), selected.vehicleNo],
                [t('mo_helpers_label'), selected.helpers && selected.helpers !== '0' ? selected.helpers : ''],
                [t('mo_submitted_label'), selected.submitted],
              ].filter(([, v]) => v).map(([k, v]) => (
                <View key={k} style={{ flexDirection: 'row', justifyContent: 'space-between', gap: space.s4 }}>
                  <AppText variant="bodySm" tone="tertiary">{k}</AppText>
                  <AppText variant="bodyMd" weight="500" style={{ flex: 1, textAlign: 'right' }}>{v}</AppText>
                </View>
              ))}
            </View>
            {selected.notes ? (
              <View style={{ marginTop: space.s3, backgroundColor: colors.canvas1, borderRadius: 12, padding: space.s3 }}>
                <AppText variant="bodySm" tone="secondary">{selected.notes}</AppText>
              </View>
            ) : null}
            {selected.status === 'pending' && !selected.cancelled ? (
              <>
                <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s4 }}>
                  {t('mo_waiting')}
                </AppText>
                <Button
                  title={t('mo_cancel')}
                  kind="destructive"
                  loading={cancelling}
                  onPress={() => void cancelRequest()}
                  style={{ marginTop: space.s3 }}
                />
              </>
            ) : null}
            {sheetError ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s3 }}>{sheetError}</AppText> : null}
          </>
        ) : null}
      </Sheet>
    </Screen>
  );
}
